/** Domain constants shared across the data and UI layers. */

export const SEVERITIES = ['Critical', 'Major', 'Minor', 'Patch', 'Unknown'] as const;
export type Severity = (typeof SEVERITIES)[number];

/**
 * SVG fill/stroke colors reference the theme tokens (styles/index.css) via
 * CSS variables so charts recolor automatically with light/dark mode.
 */
export const SEVERITY_COLOR: Record<Severity, string> = {
  Critical: 'var(--color-critical)',
  Major: 'var(--color-major)',
  Minor: 'var(--color-minor)',
  Patch: 'var(--color-patch)',
  Unknown: 'var(--color-unknown)',
};

/** Lower rank sorts first (most severe first). */
export const SEVERITY_RANK: Record<Severity, number> = {
  Critical: 0,
  Major: 1,
  Minor: 2,
  Patch: 3,
  Unknown: 4,
};

export const ACCENT = {
  brand: 'var(--color-brand)',
  accent: 'var(--color-accent)',
  security: 'var(--color-security)',
  breaking: 'var(--color-breaking)',
} as const;

export const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'sev', label: 'Severity' },
  { value: 'product', label: 'Product A–Z' },
  { value: 'features', label: 'Most features' },
] as const;

export type SortKey = (typeof SORT_OPTIONS)[number]['value'];

export const DEFAULT_API_BASE =
  // `||` (not `??`) so a set-but-empty build var falls back instead of yielding ''.
  import.meta.env.VITE_API_BASE ||
  'https://cms-viewpoint-release-hub-cnb9huesdghva6gs.eastus2-01.azurewebsites.net';

/**
 * The X-API-Key sent with every export request, injected at build time. The
 * dev/prod hubs gate the export feed behind this key, so without it the feed
 * returns 403 and the app silently falls back to sample data. Baking it in
 * lets an authenticated user see live data on login without pasting a key.
 * NOTE: this ships in the public JS bundle and is therefore extractable — it
 * is a read-only access gate, not a secret.
 */
export const DEFAULT_API_KEY = import.meta.env.VITE_API_KEY ?? '';

export const PAGE_SIZE = 12;

/** localStorage keys. */
export const STORAGE_KEYS = {
  apiBase: 'ro.apiBase',
  apiKey: 'ro.apiKey',
  theme: 'ro.theme',
} as const;

/** UI theme. Dark is the product default. */
export type Theme = 'light' | 'dark';
export const DEFAULT_THEME: Theme = 'dark';
